import { useState } from "react";

import type {
  AssignedOperator,
  Event,
  Operator,
  OverlapConflict
} from "@attendik/types/admin-schemas";

import { api } from "../../api/client.js";
import { ApiError, isApiError } from "../../api/errors.js";
import { Button } from "../../components/ui/button.js";
import { Card } from "../../components/ui/card.js";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from "../../components/ui/dialog.js";
import { Select } from "../../components/ui/select.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../components/ui/table.js";
import { EmptyState, ErrorState, LoadingState } from "../../components/states.js";
import { formatRange } from "../../lib/format.js";
import { useAsync } from "../../lib/useAsync.js";

interface OperatorsData {
  /**
   * Assigned operators, each carrying `other_active_events_count` computed
   * server-side in the list query (no per-row round-trip).
   */
  assigned: AssignedOperator[];
  available: Operator[];
}

/**
 * Operators tab — assignment matrix with overlap blocking (build plan P2-T8b).
 * On a 409 OVERLAP the conflicting events are shown in a modal; the user must
 * explicitly resolve them (unassign from the conflicting event) — never a
 * silent retry.
 */
export function OperatorsTab({ event }: { event: Event }): JSX.Element {
  const [assignError, setAssignError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{
    operatorId: string;
    operatorName: string;
    events: OverlapConflict[];
  } | null>(null);

  const { data, loading, error, reload } = useAsync<OperatorsData>(async () => {
    const [assigned, allOperators] = await Promise.all([
      api.listEventOperators(event.id),
      api.listOperators(event.customer_id)
    ]);
    const assignedIds = new Set(assigned.map((o) => o.id));
    const available = allOperators.filter(
      (o) => !assignedIds.has(o.id) && o.status === "ACTIVE"
    );
    return { assigned, available };
  }, [event.id, event.customer_id]);

  const [selectedOperator, setSelectedOperator] = useState("");

  const handleAssign = async (): Promise<void> => {
    if (!selectedOperator) {
      return;
    }
    setAssignError(null);
    const operator = data?.available.find((o) => o.id === selectedOperator);
    try {
      await api.assignEventOperator(event.id, selectedOperator);
      setSelectedOperator("");
      reload();
    } catch (err) {
      if (err instanceof ApiError && err.code === "OVERLAP" && err.conflicts) {
        setConflict({
          operatorId: selectedOperator,
          operatorName: operator?.name ?? "Operator",
          events: err.conflicts
        });
      } else if (isApiError(err) && err.code === "CROSS_CUSTOMER") {
        setAssignError("That operator belongs to a different customer.");
      } else {
        setAssignError(
          isApiError(err) ? err.message : "Could not assign the operator."
        );
      }
    }
  };

  const handleUnassign = async (operatorId: string): Promise<void> => {
    setAssignError(null);
    try {
      await api.unassignEventOperator(event.id, operatorId);
      reload();
    } catch (err) {
      setAssignError(
        isApiError(err) ? err.message : "Could not unassign the operator."
      );
    }
  };

  const handleResolveConflict = async (conflictEventId: string): Promise<void> => {
    if (!conflict) {
      return;
    }
    // Explicitly unassign the operator from the conflicting event.
    await api.unassignEventOperator(conflictEventId, conflict.operatorId);
    setConflict((prev) =>
      prev
        ? { ...prev, events: prev.events.filter((e) => e.id !== conflictEventId) }
        : null
    );
  };

  if (loading) {
    return <LoadingState />;
  }
  if (error || !data) {
    return <ErrorState message={error ?? "Failed to load."} onRetry={reload} />;
  }

  return (
    <div className="space-y-6">
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 space-y-1.5">
            <label
              htmlFor="assign-operator"
              className="text-sm font-medium text-muted-foreground"
            >
              Assign an operator
            </label>
            <Select
              id="assign-operator"
              value={selectedOperator}
              onChange={(e) => setSelectedOperator(e.target.value)}
            >
              <option value="">Select an operator…</option>
              {data.available.map((op) => (
                <option key={op.id} value={op.id}>
                  {op.name} ({op.email})
                </option>
              ))}
            </Select>
          </div>
          <Button onClick={handleAssign} disabled={!selectedOperator}>
            Assign
          </Button>
        </div>
        {data.available.length === 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">
            All of this customer's active operators are already assigned.
          </p>
        ) : null}
        {assignError ? (
          <p role="alert" className="mt-2 text-sm text-destructive">
            {assignError}
          </p>
        ) : null}
      </Card>

      <Card>
        {data.assigned.length === 0 ? (
          <EmptyState
            title="No operators assigned"
            description="Assign operators so they can scan badges for this event."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Operator</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Other active events</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.assigned.map((op) => {
                const otherCount = op.other_active_events_count;
                return (
                  <TableRow key={op.id}>
                    <TableCell className="font-medium">{op.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {op.email}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {otherCount === 0 ? "—" : `${otherCount}`}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUnassign(op.id)}
                      >
                        Unassign
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {conflict ? (
        <Dialog
          open
          onClose={() => setConflict(null)}
          labelledBy="overlap-conflict-title"
        >
          <DialogHeader
            id="overlap-conflict-title"
            title="Assignment conflict"
            description={`${conflict.operatorName} is already assigned to event(s) whose schedule overlaps this one.`}
            onClose={() => setConflict(null)}
          />
          <DialogBody>
            {conflict.events.length === 0 ? (
              <p className="text-sm text-emerald-700">
                All conflicts cleared. You can now assign the operator.
              </p>
            ) : (
              <ul className="space-y-2">
                {conflict.events.map((conf) => (
                  <li
                    key={conf.id}
                    className="flex items-center justify-between gap-3 rounded-md border border-border p-3"
                  >
                    <div>
                      <p className="text-sm font-medium">{conf.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatRange(conf.active_from, conf.active_to)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResolveConflict(conf.id)}
                    >
                      Unassign there
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-muted-foreground">
              An operator cannot cover two events at the same time. Unassign
              them from the conflicting event, then try the assignment again.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConflict(null)}>
              Close
            </Button>
            <Button
              disabled={conflict.events.length > 0}
              onClick={async () => {
                const operatorId = conflict.operatorId;
                setConflict(null);
                setSelectedOperator(operatorId);
                try {
                  await api.assignEventOperator(event.id, operatorId);
                  setSelectedOperator("");
                  reload();
                } catch (err) {
                  setAssignError(
                    isApiError(err) ? err.message : "Could not assign the operator."
                  );
                }
              }}
            >
              Retry assignment
            </Button>
          </DialogFooter>
        </Dialog>
      ) : null}
    </div>
  );
}
